// engine-exception: the workflow designer is a node canvas (@xyflow/react) over workflow.definition with a per-node property inspector, versions, publish, test-run and a run log — not a table/form/detail of one resource
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactFlow, Background, Controls, MiniMap, addEdge, useEdgesState, useNodesState, Handle, Position, type Connection, type Edge, type Node, type NodeProps } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useAction, useApiQuery, useResource, useUpdate } from '../../engine/api/hooks';
import { AutoForm } from '../../engine/forms/AutoForm';
import { Badge, EmptyState, ErrorBanner, PageHeader, Skeleton } from '../../engine/design/primitives/Misc';
import { Button, IconButton } from '../../engine/design/primitives/Button';
import { Field, Input, Select } from '../../engine/design/primitives/Input';
import { Dialog, Drawer } from '../../engine/design/primitives/Overlay';
import { Tabs } from '../../engine/design/primitives/Tabs';
import { Icon } from '../../engine/design/icons';
import { formatDateTime } from '../../lib/format';
import { emptyDefinition, NODE_ICON, outcomesOfType, PALETTE, schemaToFields, validateLocally, type Definition, type GraphEdge, type GraphNode, type LocalIssue, type NodeType } from './workflow/nodeTypes';

// Workflow designer: the canvas IS the definition (nodes + edges with outcome labels).
// Palette adds typed nodes; selecting a node opens the property inspector (AutoForm over the
// node's server JSON Schema); edges leaving an outcome node are labelled from a Select;
// validation is inline (local) and server-side (POST validate) before Publish; Versions,
// Test-with-ticket (dry path) and the run log (workflow_run + steps) live in the side drawer.

type FlowNode = Node<{ label: string; type: NodeType; invalid: boolean }>;
type Row = Record<string, unknown>;

function toFlow(def: Definition, issues: LocalIssue[]): { nodes: FlowNode[]; edges: Edge[] } {
  const bad = new Set(issues.map((i) => i.nodeId).filter(Boolean));
  const badEdges = new Set(issues.map((i) => i.edgeId).filter(Boolean));
  return {
    nodes: def.nodes.map((n, i) => ({ id: n.id, type: 'sd', position: n.position ?? { x: i * 240, y: 0 }, data: { label: n.name, type: n.type, invalid: bad.has(n.id) } })),
    edges: def.edges.map((e) => ({ id: e.id, source: e.from, target: e.to, label: e.label, animated: Boolean(e.label), className: badEdges.has(e.id) ? 'sd-edge-invalid' : undefined })),
  };
}

function SdNode({ data, selected }: NodeProps<FlowNode>) {
  return (
    <div className={['hf-card', 'pad', 'sd-node', selected && 'selected', data.invalid && 'invalid'].filter(Boolean).join(' ')} style={{ minWidth: 160, padding: 'var(--s-2) var(--s-3)', borderColor: data.invalid ? 'var(--neg)' : selected ? 'var(--accent)' : undefined }}>
      {data.type !== 'trigger' && <Handle type="target" position={Position.Left} />}
      <div className="hf-row" style={{ fontSize: 'var(--t-12)' }}>
        <Icon name={NODE_ICON[data.type]} size={14} />
        <span className="hf-caps">{data.type.replace(/_/g, ' ')}</span>
      </div>
      <div style={{ fontWeight: 600 }}>{data.label}</div>
      {data.type !== 'end' && <Handle type="source" position={Position.Right} />}
    </div>
  );
}
const nodeTypes = { sd: SdNode };

export function WorkflowDesignerScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const wfQ = useResource<Row>('workflows', id);
  const schemas = useApiQuery<{ data: Record<string, unknown>; events: string[] }>(['workflow-node-schemas'], '/v1/workflows/node-schemas', { staleTime: 60 * 60_000 });
  const versions = useApiQuery<{ data: Row[] }>(['workflow-versions', id], `/v1/workflows/${id}/versions`, { enabled: Boolean(id) });
  const runs = useApiQuery<{ data: Row[] }>(['workflow-runs', id], `/v1/workflows/${id}/runs`, { enabled: Boolean(id) });
  const update = useUpdate('workflows', { toast: { success: 'Draft saved' } });
  const publish = useAction('workflows', 'publish', { toast: { success: 'Published' } });
  const validate = useAction<{ valid: boolean; issues: { path: string; message: string; nodeId?: string }[] }>('workflows', 'validate');
  const test = useAction<{ trigger_matches: boolean; path: { node_id: string; name: string; type: string; outcome?: string }[] }>('workflows', 'test');

  const [def, setDef] = useState<Definition | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<string | null>(null);
  const [serverIssues, setServerIssues] = useState<{ message: string; nodeId?: string }[]>([]);
  const [drawer, setDrawer] = useState<'versions' | 'runs' | null>(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testTicket, setTestTicket] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (wfQ.data && !def) {
      const raw = wfQ.data['definition'] as Definition | Record<string, never> | null;
      setDef(raw && Array.isArray((raw as Definition).nodes) ? (raw as Definition) : emptyDefinition((wfQ.data['trigger_event'] as string | null) ?? 'ticket.created'));
    }
  }, [wfQ.data, def]);

  const localIssues = useMemo(() => (def ? validateLocally(def) : []), [def]);
  const issues = useMemo(() => [...localIssues, ...serverIssues], [localIssues, serverIssues]);
  const flow = useMemo(() => (def ? toFlow(def, issues) : { nodes: [], edges: [] }), [def, issues]);
  const [nodes, setNodes, onNodesChange] = useNodesState<FlowNode>(flow.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(flow.edges);
  useEffect(() => {
    setNodes(flow.nodes);
    setEdges(flow.edges);
  }, [flow, setNodes, setEdges]);

  const mutate = useCallback((fn: (d: Definition) => Definition) => {
    setDef((d) => (d ? fn(d) : d));
    setDirty(true);
    setServerIssues([]);
  }, []);

  const addNode = (type: NodeType) => {
    const entry = PALETTE.find((p) => p.type === type)!;
    const nid = `n_${type}_${Math.random().toString(36).slice(2, 7)}`;
    mutate((d) => ({ ...d, nodes: [...d.nodes, { id: nid, type, name: entry.label, position: { x: 200 + d.nodes.length * 40, y: 120 + (d.nodes.length % 4) * 90 }, config: structuredClone(entry.defaultConfig) }] }));
    setSelected(nid);
  };
  const onConnect = useCallback((c: Connection) => {
    if (!c.source || !c.target) return;
    const eid = `e_${Math.random().toString(36).slice(2, 8)}`;
    mutate((d) => {
      const from = d.nodes.find((n) => n.id === c.source);
      const labels = from ? outcomesOfType(from.type, from.config) : undefined;
      const used = new Set(d.edges.filter((e) => e.from === c.source).map((e) => e.label));
      const label = labels?.find((l) => !used.has(l));
      return { ...d, edges: [...d.edges, { id: eid, from: c.source!, to: c.target!, ...(label ? { label } : {}) }] };
    });
    setEdges((es) => addEdge({ ...c, id: eid }, es));
  }, [mutate, setEdges]);

  const node = def?.nodes.find((n) => n.id === selected) ?? null;
  const edge = def?.edges.find((e) => e.id === selectedEdge) ?? null;
  const nodeFields = useMemo(() => (node && schemas.data ? schemaToFields(schemas.data.data[node.type] as never) : []), [node, schemas.data]);

  const save = async () => {
    if (!def) return;
    // Persist canvas positions.
    const positioned: Definition = { ...def, nodes: def.nodes.map((n) => ({ ...n, position: nodes.find((x) => x.id === n.id)?.position ?? n.position })) };
    await update.mutateAsync({ id, body: { definition: positioned } });
    setDef(positioned);
    setDirty(false);
  };
  const checkOnServer = async () => {
    if (!def) return false;
    const r = await validate.mutateAsync({ body: { definition: def } });
    setServerIssues(r.issues.map((i) => ({ message: i.message, nodeId: i.nodeId })));
    return r.valid;
  };

  if (wfQ.error) return <ErrorBanner error={wfQ.error} action={<Button size="sm" variant="ghost" onClick={() => navigate('/workflows')}>Back</Button>} />;
  if (!wfQ.data || !def) return <div className="hf-page"><Skeleton lines={8} /></div>;
  const wf = wfQ.data;

  return (
    <div className="hf-page" style={{ maxWidth: 'none' }}>
      <PageHeader
        compact
        breadcrumb={[{ label: 'Workflows', to: '/workflows' }, { label: String(wf['name']) }]}
        title={String(wf['name'])}
        badge={<Badge tone={wf['status'] === 'published' ? 'pos' : wf['status'] === 'archived' ? 'muted' : 'warn'}>{String(wf['status'])} v{String(wf['version'])}</Badge>}
        subtitle={dirty ? 'Unsaved changes' : `Trigger: ${def.trigger.type === 'event' ? def.trigger.event : 'manual'}`}
        actions={
          <div className="hf-row">
            <Button variant="ghost" icon="history" onClick={() => setDrawer('versions')}>Versions</Button>
            <Button variant="ghost" icon="play" onClick={() => setDrawer('runs')}>Runs</Button>
            <Button variant="outline" icon="check" onClick={() => void checkOnServer()} busy={validate.isPending}>Validate</Button>
            <Button variant="outline" icon="ticket" onClick={() => setTestOpen(true)}>Test</Button>
            <Button icon="save" onClick={() => void save()} busy={update.isPending} disabled={!dirty}>Save draft</Button>
            <Button variant="primary" icon="upload" busy={publish.isPending} disabled={issues.length > 0}
              onClick={async () => { if (dirty) await save(); if (await checkOnServer()) publish.mutate({ id, body: {} }, { onSuccess: () => { void wfQ.refetch(); void versions.refetch(); } }); }}>
              Publish
            </Button>
          </div>
        }
      />
      {issues.length > 0 && (
        <div className="hf-banner error" role="alert" style={{ marginBottom: 'var(--s-3)' }}>
          <Icon name="alert-triangle" size={14} /> {issues.length} issue{issues.length === 1 ? '' : 's'}: {issues.slice(0, 3).map((i) => i.message).join(' · ')}{issues.length > 3 ? ' …' : ''}
        </div>
      )}
      <div className="hf-row" style={{ alignItems: 'stretch', gap: 'var(--s-4)', minHeight: 560 }}>
        <aside className="hf-stack" style={{ width: 200, gap: 'var(--s-1)' }} aria-label="Node palette">
          <div className="hf-caps">Trigger</div>
          <Field label="Event" id="sd-wf-event">
            <Select id="sd-wf-event" size="sm" value={def.trigger.event ?? 'manual'} onChange={(e) => mutate((d) => ({ ...d, trigger: e.target.value === 'manual' ? { type: 'manual' } : { ...d.trigger, type: 'event', event: e.target.value } }))}
              options={[{ value: 'manual', label: 'Manual' }, ...(schemas.data?.events ?? ['ticket.created']).map((e) => ({ value: e, label: e }))]} />
          </Field>
          <div className="hf-caps" style={{ marginTop: 'var(--s-2)' }}>Add node</div>
          {PALETTE.map((p) => (
            <Button key={p.type} size="sm" variant="ghost" icon={p.icon} title={p.hint} onClick={() => addNode(p.type)} style={{ justifyContent: 'flex-start' }}>{p.label}</Button>
          ))}
        </aside>
        <div className="hf-card hf-grow" style={{ minHeight: 560 }} aria-label="Workflow canvas">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={(c) => { onNodesChange(c); if (c.some((x) => x.type === 'position')) setDirty(true); }}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(_e, n) => { setSelected(n.id); setSelectedEdge(null); }}
            onEdgeClick={(_e, e) => { setSelectedEdge(e.id); setSelected(null); }}
            onPaneClick={() => { setSelected(null); setSelectedEdge(null); }}
            onEdgesDelete={(del) => mutate((d) => ({ ...d, edges: d.edges.filter((e) => !del.some((x) => x.id === e.id)) }))}
            onNodesDelete={(del) => mutate((d) => ({ ...d, nodes: d.nodes.filter((n) => !del.some((x) => x.id === n.id) || n.type === 'trigger'), edges: d.edges.filter((e) => !del.some((x) => x.id === e.from || x.id === e.to)) }))}
            fitView
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={16} />
            <Controls />
            <MiniMap pannable zoomable />
          </ReactFlow>
        </div>
        <aside className="hf-card pad hf-stack" style={{ width: 340 }} aria-label="Property inspector">
          {node ? (
            <>
              <div className="hf-between">
                <div className="hf-row"><Icon name={NODE_ICON[node.type]} size={16} /><span className="hf-caps">{node.type.replace(/_/g, ' ')}</span></div>
                {node.type !== 'trigger' && <IconButton icon="trash" label="Delete node" size="sm" onClick={() => { mutate((d) => ({ ...d, nodes: d.nodes.filter((n) => n.id !== node.id), edges: d.edges.filter((e) => e.from !== node.id && e.to !== node.id) })); setSelected(null); }} />}
              </div>
              <Field label="Name" id="sd-node-name"><Input id="sd-node-name" value={node.name} onChange={(e) => mutate((d) => ({ ...d, nodes: d.nodes.map((n) => (n.id === node.id ? { ...n, name: e.target.value } : n)) }))} /></Field>
              {nodeFields.length > 0 ? (
                <AutoForm key={node.id} fields={nodeFields} mode="edit" layout="stack" initial={node.config} submitLabel="Apply" autoFocus={false}
                  onSubmit={(_changed, all) => mutate((d) => ({ ...d, nodes: d.nodes.map((n) => (n.id === node.id ? { ...n, config: { ...n.config, ...all } } : n)) }))} />
              ) : schemas.isLoading ? <Skeleton lines={3} /> : <span className="hf-muted">No properties.</span>}
              {issues.filter((i) => i.nodeId === node.id).map((i, k) => <div key={k} className="hf-error" role="alert">{i.message}</div>)}
            </>
          ) : edge ? (
            <>
              <div className="hf-caps">Edge</div>
              {(() => {
                const from = def.nodes.find((n) => n.id === edge.from);
                const labels = from ? outcomesOfType(from.type, from.config) : undefined;
                return labels ? (
                  <Field label="Outcome" id="sd-edge-label">
                    <Select id="sd-edge-label" value={edge.label ?? ''} placeholder="Pick an outcome" options={labels.map((l) => ({ value: l, label: l }))}
                      onChange={(e) => mutate((d) => ({ ...d, edges: d.edges.map((x) => (x.id === edge.id ? { ...x, label: e.target.value || undefined } : x)) }))} />
                  </Field>
                ) : <span className="hf-muted">{from?.name ?? '?'} → {def.nodes.find((n) => n.id === edge.to)?.name ?? '?'}</span>;
              })()}
              <Button size="sm" variant="danger" icon="trash" onClick={() => { mutate((d) => ({ ...d, edges: d.edges.filter((x) => x.id !== edge.id) })); setSelectedEdge(null); }}>Remove edge</Button>
            </>
          ) : (
            <EmptyState title="Select a node" hint="Click a node to edit its properties, or an edge to label its outcome." icon="workflow" />
          )}
        </aside>
      </div>

      <Drawer open={drawer !== null} onClose={() => setDrawer(null)} title={drawer === 'versions' ? 'Versions' : 'Runs'} size="lg">
        {drawer === 'versions' && (
          <ul className="hf-stack" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {(versions.data?.data ?? []).map((v) => (
              <li key={String(v['id'])} className="hf-between"><span><Badge tone="muted">v{String(v['version'])}</Badge> <span className="hf-muted hf-num">{formatDateTime(String(v['created_at']))}</span></span>
                <Button size="sm" variant="ghost" onClick={() => { setDef(v['definition'] as Definition); setDirty(true); setDrawer(null); }}>Load into draft</Button></li>
            ))}
            {(versions.data?.data ?? []).length === 0 && <EmptyState title="Never published" icon="history" />}
          </ul>
        )}
        {drawer === 'runs' && <RunLog runs={runs.data?.data ?? []} />}
      </Drawer>

      <Dialog open={testOpen} onClose={() => setTestOpen(false)} title="Test with a ticket" description="Evaluates the trigger and walks the graph without applying anything."
        footer={<><Button variant="ghost" onClick={() => setTestOpen(false)}>Close</Button><Button variant="primary" busy={test.isPending} disabled={!testTicket} onClick={() => test.mutate({ id, body: { ticket_id: testTicket } })}>Run test</Button></>}>
        <Field label="Ticket id" id="sd-test-ticket"><Input id="sd-test-ticket" value={testTicket} onChange={(e) => setTestTicket(e.target.value)} placeholder="uuid" /></Field>
        {test.data && (
          <div className="hf-stack" style={{ marginTop: 'var(--s-3)' }}>
            <Badge tone={test.data.trigger_matches ? 'pos' : 'warn'}>{test.data.trigger_matches ? 'Trigger matches' : 'Trigger would not fire'}</Badge>
            <ol className="hf-stack" style={{ gap: 'var(--s-1)', margin: 0, paddingLeft: 'var(--s-5)' }}>
              {test.data.path.map((p) => <li key={p.node_id}><span className="hf-caps">{p.type.replace(/_/g, ' ')}</span> {p.name}{p.outcome ? <Badge tone="info" dot={false}>{p.outcome}</Badge> : null}</li>)}
            </ol>
          </div>
        )}
      </Dialog>
    </div>
  );
}

function RunLog({ runs }: { runs: Row[] }) {
  const [open, setOpen] = useState<string | null>(null);
  const [tab, setTab] = useState('all');
  const steps = useApiQuery<{ steps: Row[] }>(['workflow-run-steps', open ?? ''], `/v1/workflow-runs/${open}/steps`, { enabled: Boolean(open) });
  const shown = runs.filter((r) => tab === 'all' || r['state'] === tab);
  if (runs.length === 0) return <EmptyState title="No runs yet" icon="play" />;
  return (
    <div className="hf-stack">
      <Tabs variant="pills" value={tab} onChange={setTab} aria-label="Run state" tabs={[{ key: 'all', label: 'All' }, { key: 'running', label: 'Running' }, { key: 'waiting', label: 'Waiting' }, { key: 'completed', label: 'Completed' }, { key: 'failed', label: 'Failed' }]} />
      <ul className="hf-stack" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {shown.map((r) => (
          <li key={String(r['id'])} className="hf-stack" style={{ gap: 'var(--s-1)' }}>
            <div className="hf-between">
              <span className="hf-row"><Badge tone={r['state'] === 'completed' ? 'pos' : r['state'] === 'failed' ? 'neg' : r['state'] === 'waiting' ? 'warn' : 'info'}>{String(r['state'])}</Badge><span className="hf-mono hf-muted">{String(r['entity_id']).slice(0, 8)}</span><span className="hf-muted hf-num">{formatDateTime(String(r['started_at']))}</span></span>
              <Button size="sm" variant="ghost" onClick={() => setOpen(open === r['id'] ? null : String(r['id']))}>{open === r['id'] ? 'Hide' : 'Steps'}</Button>
            </div>
            {r['error'] ? <div className="hf-error">{String(r['error'])}</div> : null}
            {open === r['id'] && (steps.isLoading ? <Skeleton lines={3} /> : (
              <ol className="hf-stack" style={{ gap: 'var(--s-1)', margin: 0, paddingLeft: 'var(--s-5)' }}>
                {(steps.data?.steps ?? []).map((s) => <li key={String(s['id'])}><Badge tone={s['status'] === 'ok' ? 'pos' : s['status'] === 'error' ? 'neg' : 'muted'} dot={false}>{String(s['status'])}</Badge> <span className="hf-caps">{String(s['node_type']).replace(/_/g, ' ')}</span> {String(s['node_id'])}{s['error'] ? <span className="hf-error"> — {String(s['error'])}</span> : null}</li>)}
              </ol>
            ))}
          </li>
        ))}
      </ul>
    </div>
  );
}

export type { GraphEdge, GraphNode };
